import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { api } from "@/lib/api";

const FILL = { red: "url(#hatch-red)", yellow: "rgba(245,158,11,0.38)", green: "rgba(16,185,129,0.30)", grey: "rgba(113,113,122,0.16)" };
const STROKE = { red: "#dc2626", yellow: "#d97706", green: "#059669", grey: "#71717a" };

export const STATE_LABEL = { red: "Criticità aperta", yellow: "Attenzione / intervento programmato", green: "Verificata, senza criticità", grey: "Dati non verificati" };

export const MapLegend = () => (
  <div className="flex flex-wrap gap-3 text-xs font-semibold" data-testid="map-legend">
    {Object.entries(STATE_LABEL).map(([k, v]) => (
      <span key={k} className="inline-flex items-center gap-1.5">
        <span className="h-3.5 w-5 rounded-sm border-2" style={{ borderColor: STROKE[k], borderStyle: k === "red" ? "dashed" : "solid", background: k === "red" ? "rgba(239,68,68,0.35)" : FILL[k] }} />{v}
      </span>
    ))}
  </div>
);

const pts = (p) => p.map((x) => x.join(",")).join(" ");

export const MapCanvas = ({ plan, areas, selectedId, onSelect, mode, draft, onCanvasPoint, editPoints, onEditPoints }) => {
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const svg = useRef();
  const drag = useRef(null);

  useEffect(() => {
    let url;
    api.get(`/planimetrie/${plan.id}/image`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); });
    return () => url && URL.revokeObjectURL(url);
  }, [plan.id]);

  const toSvg = (e) => {
    const p = svg.current.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const r = p.matrixTransform(svg.current.getScreenCTM().inverse());
    return [Math.round(r.x), Math.round(r.y)];
  };
  const move = (e) => {
    if (drag.current === null) return;
    const next = editPoints.map((q, i) => (i === drag.current ? toSvg(e) : q));
    onEditPoints(next);
  };

  return (
    <div className="relative">
      <div className="absolute right-3 top-3 z-10 flex flex-col gap-2">
        <button onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="rounded-xl border bg-white p-2 shadow" data-testid="map-zoom-in"><ZoomIn className="h-5 w-5" /></button>
        <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="rounded-xl border bg-white p-2 shadow" data-testid="map-zoom-out"><ZoomOut className="h-5 w-5" /></button>
      </div>
      <div className="max-h-[78vh] overflow-auto rounded-2xl border bg-white" data-testid="map-canvas">
        <div className="relative" style={{ width: `${zoom * 100}%` }}>
          {src ? <img src={src} alt="Planimetria" className="block w-full select-none" draggable={false} /> : <div style={{ aspectRatio: `${plan.width}/${plan.height}` }} className="animate-pulse bg-muted" />}
          <svg ref={svg} viewBox={`0 0 ${plan.width} ${plan.height}`} className="absolute inset-0 h-full w-full"
            onClick={(e) => mode === "draw" && onCanvasPoint(toSvg(e))} onPointerMove={move} onPointerUp={() => { drag.current = null; }}
            style={{ cursor: mode === "draw" ? "crosshair" : "default" }}>
            <defs>
              <pattern id="hatch-red" width="18" height="18" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                <rect width="18" height="18" fill="rgba(239,68,68,0.22)" /><line x1="0" y1="0" x2="0" y2="18" stroke="rgba(220,38,38,0.75)" strokeWidth="7" />
              </pattern>
            </defs>
            {areas.map((a) => {
              const sel = a.id === selectedId;
              const p = sel && mode === "edit" && editPoints ? editPoints : a.points;
              return (
                <polygon key={a.id} points={pts(p)} data-testid={`map-area-${a.code}`} data-state={a.state}
                  fill={FILL[a.state]} stroke={sel ? "#1d4ed8" : STROKE[a.state]} strokeWidth={sel ? 6 : a.state === "grey" ? 1.5 : 3}
                  strokeDasharray={a.state === "red" && !sel ? "12 7" : undefined}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  onClick={(e) => { if (mode !== "draw") { e.stopPropagation(); onSelect(a); } }} />
              );
            })}
            {mode === "edit" && editPoints?.map((q, i) => (
              <circle key={i} cx={q[0]} cy={q[1]} r={14} fill="#1d4ed8" stroke="white" strokeWidth="4" style={{ touchAction: "none", cursor: "grab" }}
                data-testid={`map-vertex-${i}`} onPointerDown={(e) => { e.stopPropagation(); e.target.setPointerCapture?.(e.pointerId); drag.current = i; }} />
            ))}
            {mode === "draw" && draft.length > 0 && (
              <>
                <polyline points={pts(draft)} fill="rgba(29,78,216,0.15)" stroke="#1d4ed8" strokeWidth="4" strokeDasharray="8 6" />
                {draft.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={10} fill="#1d4ed8" />)}
              </>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
};
